import { useEffect } from 'react';
import { useScreenshotMode } from '../../context/ScreenshotModeContext';

const UI_TEXT = new Set([
  'account settings',
  'actions',
  'all clients',
  'billing',
  'cancel',
  'clients',
  'close',
  'dashboard',
  'delete',
  'download',
  'edit',
  'help',
  'inventory',
  'invoices',
  'loading',
  'log out',
  'logout',
  'menu',
  'my account',
  'next',
  'notifications',
  'orders',
  'previous',
  'products',
  'purchases',
  'randomized screenshot mode',
  'save',
  'search',
  'settings',
  'shipments',
  'stores',
  'submit',
  'transfers',
  'turn off',
  'users',
]);

const SENSITIVE_FIELD_PATTERN =
  /\b(client|customer|store|company|owner|product|item|sku|order|invoice|shipment|transfer|purchase|asn|vendor|supplier|user|contact|email|address|recipient|reference|identifier|tracking)\b/i;

function hash(value) {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function randomDigits(value) {
  const seed = hash(value);
  let offset = 0;
  return value.replace(/\d/g, () => String((seed + offset++ * 7) % 10));
}

function randomWords(value) {
  const adjectives = ['Blue', 'Golden', 'North', 'Rapid', 'Silver', 'Bright', 'Cedar', 'Summit'];
  const nouns = ['Harbor', 'Supply', 'Goods', 'Logistics', 'Market', 'Trading', 'Works', 'Depot'];
  const seed = hash(value);
  const replacement = `${adjectives[seed % adjectives.length]} ${
    nouns[Math.floor(seed / adjectives.length) % nouns.length]
  }`;
  return value.includes(' ') ? replacement : replacement.split(' ')[0];
}

function randomize(value) {
  const trimmed = value.trim();
  if (!trimmed || UI_TEXT.has(trimmed.toLowerCase())) return value;

  let output = value
    .replace(
      /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
      (email) => `contact${hash(email) % 997}@example.com`,
    )
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
      (id) => `00000000-0000-4000-8000-${String(hash(id)).padStart(12, '0')}`,
    )
    .replace(/\+?\d[\d\s().-]{7,}\d/g, randomDigits)
    .replace(/\b(?:SKU|PO|SO|ASN|INV|ORD|TR|RET)[-#:\s]*[A-Z0-9-]{2,}\b/gi, randomDigits);

  if (output !== value) return output;
  if (/^\s*[$€£]?\s*\d[\d,.%]*\s*$/.test(value)) return randomDigits(value);
  return randomWords(value);
}

function isDataText(node) {
  const parent = node.parentElement;
  if (!parent || parent.closest('[data-screenshot-mode-control]')) return false;
  const text = node.nodeValue?.trim();
  if (!text || UI_TEXT.has(text.toLowerCase())) return false;
  if (/@|\b[0-9a-f]{8}-[0-9a-f]{4}-/i.test(text)) return true;
  return Boolean(
    parent.closest(
      'address, [class*="product-name"], [class*="client-name"], [class*="customer-name"], [class*="store-name"], [class*="company-name"], [class*="user-name"], [class*="vendor-name"], [class*="email"], [class*="address"], [class*="identifier"], [class*="store-toggle"], [class*="dropdown-name"]',
    ),
  );
}

function randomizeTableData(clone) {
  clone.querySelectorAll('table').forEach((table) => {
    const headers = [...table.querySelectorAll('thead th')].map((header) =>
      header.textContent.trim().toLowerCase(),
    );
    if (!headers.length) return;

    table.querySelectorAll('tbody tr').forEach((row) => {
      [...row.children].forEach((cell, index) => {
        if (!SENSITIVE_FIELD_PATTERN.test(headers[index] || '')) return;
        const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        nodes.forEach((node) => {
          if (node.nodeValue.trim()) node.nodeValue = randomize(node.nodeValue);
        });
      });
    });
  });
}

export function sanitizeClone(clone) {
  clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

  randomizeTableData(clone);

  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    if (isDataText(node)) node.nodeValue = randomize(node.nodeValue);
  });

  clone.querySelectorAll('input, textarea').forEach((element) => {
    if (element.value) element.value = randomize(element.value);
  });

  clone.querySelectorAll('img').forEach((image) => {
    const source = image.getAttribute('src') || '';
    if (source && !/\/skusavvy(?:-|\/)/i.test(source)) {
      image.setAttribute('src', '/skusavvy-cube-icon.png');
      image.removeAttribute('srcset');
    }
  });
}

function mapElements(source, clone) {
  const sourceElements = [source, ...source.querySelectorAll('*')];
  const cloneElements = [clone, ...clone.querySelectorAll('*')];
  const elementMap = new WeakMap();
  sourceElements.forEach((element, index) => {
    if (!cloneElements[index]) return;
    const clonedElement = cloneElements[index];
    elementMap.set(element, clonedElement);
    clonedElement.scrollTop = element.scrollTop;
    clonedElement.scrollLeft = element.scrollLeft;
  });
  return elementMap;
}

function buildOverlay(root) {
  const clone = root.cloneNode(true);
  sanitizeClone(clone);
  clone.classList.add('screenshot-randomize-overlay');
  Object.assign(clone.style, {
    background: 'var(--bs-body-bg, #0f1420)',
    inset: '0',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: '2147483000',
  });
  document.body.appendChild(clone);
  return { element: clone, elementMap: mapElements(root, clone) };
}

export default function ScreenshotRandomizer() {
  const { enabled } = useScreenshotMode();

  useEffect(() => {
    if (!enabled) return undefined;
    const root = document.querySelector('.app');
    if (!root) return undefined;

    document.body.classList.add('screenshot-randomize-active');
    let overlay = buildOverlay(root);
    let rebuildTimer = null;
    let scrollFrame = null;

    const syncScroll = (event) => {
      const source = event.target === document ? document.scrollingElement : event.target;
      const clonedElement = overlay.elementMap.get(source);
      if (!clonedElement || scrollFrame !== null) return;
      scrollFrame = requestAnimationFrame(() => {
        clonedElement.scrollTop = source.scrollTop;
        clonedElement.scrollLeft = source.scrollLeft;
        scrollFrame = null;
      });
    };

    const observer = new MutationObserver(() => {
      if (rebuildTimer !== null) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        const nextOverlay = buildOverlay(root);
        overlay.element.remove();
        overlay = nextOverlay;
        rebuildTimer = null;
      }, 300);
    });
    observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    document.addEventListener('scroll', syncScroll, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('scroll', syncScroll, true);
      if (rebuildTimer !== null) clearTimeout(rebuildTimer);
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      overlay.element.remove();
      document.body.classList.remove('screenshot-randomize-active');
    };
  }, [enabled]);

  return null;
}
